import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/MagnifyingGlass.css';

const ImageMagnifier = ({
    src,
    alt = "Image",
    style = {},
    className = "",
    magnificationLevel = 10,
    magnifierSize = 600,
    showIndicator = true,
    indicatorText = "Hover to zoom",
    indicatorPosition = "bottom-right" // "bottom-right", "top-right", "bottom-left", "top-left"
}) => {
    const [isMagnifying, setIsMagnifying] = useState(false);
    const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
    const imageRef = useRef(null);

    const handleImageMouseEnter = useCallback(() => {
        if (!src) return;
        setIsMagnifying(true);
    }, [src]);

    const handleImageMouseLeave = useCallback(() => {
        setIsMagnifying(false);
    }, []);

    const handleImageMouseMove = useCallback((e) => {
        if (!src || !imageRef.current) return;

        const rect = imageRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Ensure coordinates are within image bounds
        const boundedX = Math.max(0, Math.min(x, rect.width));
        const boundedY = Math.max(0, Math.min(y, rect.height));

        // Calculate magnification position
        const magnifierRadius = magnifierSize / 2;

        // Calculate the position to show in the magnifier
        const magnifierX = -boundedX * magnificationLevel + magnifierRadius;
        const magnifierY = -boundedY * magnificationLevel + magnifierRadius;

        setMagnifierPosition({ x: magnifierX, y: magnifierY });
    }, [src, magnificationLevel, magnifierSize]);

    // Get indicator position styles
    const getIndicatorPosition = () => {
        const baseStyles = {
            position: 'absolute',
            zIndex: 5,
            pointerEvents: 'none'
        };

        switch (indicatorPosition) {
            case 'top-right':
                return { ...baseStyles, top: '15px', right: '15px' };
            case 'top-left':
                return { ...baseStyles, top: '15px', left: '15px' };
            case 'bottom-left':
                return { ...baseStyles, bottom: '15px', left: '15px' };
            case 'bottom-right':
            default:
                return { ...baseStyles, bottom: '15px', right: '15px' };
        }
    };

    return (
        <div className="image-magnifier-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
            <div
                className="magnifying-glass-container"
                onMouseEnter={handleImageMouseEnter}
                onMouseLeave={handleImageMouseLeave}
                onMouseMove={handleImageMouseMove}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <img
                    ref={imageRef}
                    src={src}
                    alt={alt}
                    className={`magnifying-glass-image ${className}`}
                    style={{
                        ...style,
                        display: 'block'
                    }}
                />

                {/* Stylish magnifying glass indicator */}
                {!isMagnifying && showIndicator && src && (
                    <div
                        style={{
                            ...getIndicatorPosition(),
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            padding: '10px 16px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            transform: 'translateY(0px)',
                            transition: 'all 0.3s ease'
                        }}
                        className="magnify-indicator"
                    >
                        <div style={{
                            width: '18px',
                            height: '18px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px'
                        }}>
                            🔍
                        </div>
                        {indicatorText}
                    </div>
                )}
            </div>

            {/* Magnifying glass overlay - appears in center of screen using portal */}
            {isMagnifying && src && createPortal(
                <div
                    className={`magnifying-glass-overlay ${isMagnifying ? 'active' : ''}`}
                    style={{
                        width: magnifierSize,
                        height: magnifierSize
                    }}
                >
                    <img
                        src={src}
                        alt={`Magnified ${alt}`}
                        className="magnified-image"
                        style={{
                            left: magnifierPosition.x,
                            top: magnifierPosition.y,
                            width: imageRef.current ? imageRef.current.getBoundingClientRect().width * magnificationLevel : 'auto',
                            height: imageRef.current ? imageRef.current.getBoundingClientRect().height * magnificationLevel : 'auto'
                        }}
                    />
                </div>,
                document.body
            )}
        </div>
    );
};

export default ImageMagnifier;